/* eslint-disable import/no-import-module-exports */
import { configureStore as configureStoreToolkit } from '@reduxjs/toolkit';
import throttle from 'lodash/throttle';
import localforage from 'localforage';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  PersistedState,
} from 'redux-persist';
import createHelpers from 'src/store/createHelpers';
import { saveState, saveLocalState } from 'src/utils/StorageUtils';
import { rootReducer } from 'src/store/rootReducer';
import { appAPI, userDataApi } from 'src/services/api';
import { RootState } from 'src/store/reduxTypes';
import { UsersAPIName } from 'src/constants';

// This array defines the list of reducers that will not be persisted in the local storage
const NOT_PERSISTED_STATES = [
  'appAPI',
  UsersAPIName,
  'messages',
  'payments',
  'ui',
  'settings',
  'files',
  'knowledgeBase',
  'user.data.stripeCustomer',
  'user.data.stripeSubscriptions',
  'user.data.updatingSubscription',
  'user.loadingError',
  'auth',
  'tags',
];

/**
 * This function is used to handle the state migration.
 * @param initialState: the initial state of the app
 * @param persistedState: the persisted state from the local storage
 * @returns the rehydrated state
 */
const handleStateMigration = (
  initialState: RootState,
  persistedState: RootState,
) => {
  // when persisted state is undefined, return the initial state to be the rehydrated state
  if (!persistedState) return Promise.resolve(initialState);
  // otherwise the rehydrated state = persisted state
  return Promise.resolve({
    ...persistedState,
    user: {
      ...(persistedState?.user || {}),
      // reset the user loaded state to false
      // so that on refresh we refetch the user data
      loaded: false,
      loading: false,
      isClient: initialState?.user?.isClient, // isClient should be available
      loadingError: null,
    },
    // when crm table mounts it fetches the clients custom fields
    // if they are not loaded yet (it checks for isClientCustomFieldsLoaded
    // to call get custom fields api).
    // We need to reset these loading states to false
    // so that it refetch the custom fields on page refresh
    clients: {
      ...(persistedState?.clients || {}),
      isClientCustomFieldsLoaded: false,
      isClientCustomFieldsLoading: false,
    },
    users: {
      ...(persistedState?.users || {}),
      loadedUsers: false,
    },
  });
};

export const configureStore = (
  initialState: any,
  helpersConfig: any,
  usePersister: boolean,
  keyConfig?: any,
) => {
  const { portalId, userId } = keyConfig || {};
  const appStateReducer =
    usePersister || portalId !== 'workspace' // do not add persister when portal id is workspace (login page)
      ? persistReducer(
          {
            version: 1,
            // when user has multiple portals, we need to identify
            // the persisted store by the portal id which is stored in the session data.
            // This will avoid mixing the data between the portals as well as
            key: `${portalId}-${userId}-root`,
            storage: localforage,
            debug: true,
            blacklist: NOT_PERSISTED_STATES,
            migrate: (persistedState: PersistedState) =>
              handleStateMigration(initialState, persistedState as RootState),
          },
          rootReducer,
        )
      : rootReducer;

  const helpers = createHelpers(helpersConfig);
  // https://redux.js.org/docs/api/createStore.html
  // const store = createStore(rootReducer, initialState, enhancer);
  const store = configureStoreToolkit({
    reducer: appStateReducer,
    preloadedState: initialState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: helpers,
        },
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
        immutableCheck: false, // need to fix this
      })
        .concat(appAPI.middleware)
        .concat(userDataApi.middleware),
  });

  // eslint-disable-next-line no-underscore-dangle
  store.__persistor = persistStore(store);

  // Hot reload reducers (requires Webpack or Browserify HMR to be enabled)
  if (__DEV__ && module.hot) {
    module.hot.accept('./rootReducer', () =>
      import('./rootReducer').then(reducerModule => {
        store.replaceReducer(reducerModule.rootReducer);
      }),
    );
  }

  store.subscribe(
    throttle(() => {
      const currentState = store.getState();
      saveState({
        user: currentState.user,
      });

      saveLocalState({
        clients: currentState.clients,
      });
    }, 1000),
  );

  return store;
};
type StoreType = ReturnType<typeof configureStore>;

export type AppState = ReturnType<StoreType['getState']>;

export type AppDispatch = StoreType['dispatch'];
